import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';

interface Character {
  id: string;
  name: string;
  age: string;
  occupation: string;
  personality: string;
  background: string;
  appearance: string;
  relationships: string[];
}

const CharacterSettingPage: React.FC = () => {
  const [characters, setCharacters] = useState<Character[]>([
    {
      id: '1',
      name: '林小雨',
      age: '18岁',
      occupation: '高三学生',
      personality: '内向但有主见，热爱写作，善良纯真',
      background: '普通家庭，从小喜欢文学创作，梦想成为作家',
      appearance: '长发，戴着眼镜，喜欢穿简单的T恤和牛仔裤',
      relationships: ['小美（闺蜜）', '小强（暗恋对象）', '张老师（班主任）']
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState<Character | null>(null);
  const [newCharacter, setNewCharacter] = useState<Partial<Character>>({
    name: '',
    age: '',
    occupation: '',
    personality: '',
    background: '',
    appearance: '',
    relationships: []
  });

  const handleAddCharacter = () => {
    if (newCharacter.name && newCharacter.personality) {
      const character: Character = {
        id: Date.now().toString(),
        name: newCharacter.name || '',
        age: newCharacter.age || '',
        occupation: newCharacter.occupation || '',
        personality: newCharacter.personality || '',
        background: newCharacter.background || '',
        appearance: newCharacter.appearance || '',
        relationships: newCharacter.relationships || []
      };

      setCharacters([...characters, character]);
      setNewCharacter({
        name: '',
        age: '',
        occupation: '',
        personality: '',
        background: '',
        appearance: '',
        relationships: []
      });
      setShowAddForm(false);
    }
  };

  const handleEditCharacter = (character: Character) => {
    setEditingCharacter(character);
    setNewCharacter({
      name: character.name,
      age: character.age,
      occupation: character.occupation,
      personality: character.personality,
      background: character.background,
      appearance: character.appearance,
      relationships: character.relationships
    });
    setShowAddForm(true);
  };

  const handleUpdateCharacter = () => {
    if (editingCharacter && newCharacter.name && newCharacter.personality) {
      const updatedCharacter: Character = {
        ...editingCharacter,
        name: newCharacter.name || editingCharacter.name,
        age: newCharacter.age || editingCharacter.age,
        occupation: newCharacter.occupation || editingCharacter.occupation,
        personality: newCharacter.personality || editingCharacter.personality,
        background: newCharacter.background || editingCharacter.background,
        appearance: newCharacter.appearance || editingCharacter.appearance,
        relationships: newCharacter.relationships || editingCharacter.relationships
      };

      setCharacters(characters.map(c => c.id === editingCharacter.id ? updatedCharacter : c));
      setEditingCharacter(null);
      setNewCharacter({
        name: '',
        age: '',
        occupation: '',
        personality: '',
        background: '',
        appearance: '',
        relationships: []
      });
      setShowAddForm(false);
    }
  };

  const handleDeleteCharacter = (characterId: string) => {
    if (confirm('确定要删除这个角色吗？')) {
      setCharacters(characters.filter(c => c.id !== characterId));
    }
  };

  const handleGenerateCharacter = () => {
    // 模拟AI生成角色
    const aiCharacters = [
      {
        id: Date.now().toString(),
        name: '阿杰',
        age: '28岁',
        occupation: '咖啡师',
        personality: '温柔体贴，有艺术气息，神秘感强',
        background: '从小学习咖啡艺术，曾在意大利学习，回国后经营自己的咖啡厅',
        appearance: '短发，穿着简约时尚，手指修长，眼神深邃',
        relationships: ['小林（恋人）', '咖啡常客们']
      },
      {
        id: (Date.now() + 1).toString(),
        name: '苏婉',
        age: '25岁',
        occupation: '穿越者/丞相之女',
        personality: '现代思维，聪明机智，适应力强',
        background: '现代白领意外穿越到古代，成为丞相之女',
        appearance: '古典美貌，气质温婉，眼神中带着现代人的灵动',
        relationships: ['皇帝（恋人）', '丞相（父亲）', '丫鬟']
      }
    ];

    setCharacters([...characters, ...aiCharacters]);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 面包屑导航 */}
        <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-purple-600">首页</Link>
          <span>/</span>
          <span className="text-gray-900">角色设定</span>
        </nav>

        {/* 页面标题 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">角色设定管理</h1>
          <p className="text-gray-600">创建和管理您的短剧角色，构建丰富的人物关系网络</p>
        </div>

        {/* 操作按钮 */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
          >
            ➕ 手动添加角色
          </button>
          <button
            onClick={handleGenerateCharacter}
            className="px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors"
          >
            🎭 AI生成角色
          </button>
          <Link
            to="/create"
            className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors text-center"
          >
            📝 返回剧本创作
          </Link>
        </div>

        {/* 添加角色表单 */}
        {showAddForm && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {editingCharacter ? '编辑角色' : '添加新角色'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">角色姓名</label>
                <input
                  type="text"
                  value={newCharacter.name}
                  onChange={(e) => setNewCharacter({...newCharacter, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="请输入角色姓名"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">年龄</label>
                <input
                  type="text"
                  value={newCharacter.age}
                  onChange={(e) => setNewCharacter({...newCharacter, age: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="请输入年龄"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">职业</label>
                <input
                  type="text"
                  value={newCharacter.occupation}
                  onChange={(e) => setNewCharacter({...newCharacter, occupation: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="请输入职业"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">性格特征</label>
                <input
                  type="text"
                  value={newCharacter.personality}
                  onChange={(e) => setNewCharacter({...newCharacter, personality: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="请输入性格特征"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">背景故事</label>
                <textarea
                  value={newCharacter.background}
                  onChange={(e) => setNewCharacter({...newCharacter, background: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-24 resize-none"
                  placeholder="请输入背景故事"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">外貌描述</label>
                <textarea
                  value={newCharacter.appearance}
                  onChange={(e) => setNewCharacter({...newCharacter, appearance: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-20 resize-none"
                  placeholder="请输入外貌描述"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={editingCharacter ? handleUpdateCharacter : handleAddCharacter}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                {editingCharacter ? '确认更新' : '确认添加'}
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setEditingCharacter(null);
                  setNewCharacter({
                    name: '',
                    age: '',
                    occupation: '',
                    personality: '',
                    background: '',
                    appearance: '',
                    relationships: []
                  });
                }}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                取消
              </button>
            </div>
          </div>
        )}

        {/* 角色列表 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {characters.map((character) => (
            <div key={character.id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold mr-4">
                  {character.name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{character.name}</h3>
                  <p className="text-sm text-gray-600">{character.age} · {character.occupation}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">性格特征</h4>
                  <p className="text-sm text-gray-600">{character.personality}</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-1">背景故事</h4>
                  <p className="text-sm text-gray-600">{character.background}</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-1">外貌描述</h4>
                  <p className="text-sm text-gray-600">{character.appearance}</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-1">人物关系</h4>
                  <div className="flex flex-wrap gap-2">
                    {character.relationships.map((relationship, index) => (
                      <span key={index} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                        {relationship}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => handleEditCharacter(character)}
                  className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  编辑
                </button>
                <button
                  onClick={() => handleDeleteCharacter(character.id)}
                  className="flex-1 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                >
                  删除
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* 角色关系图谱 */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">人物关系图谱</h3>
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🕸️</div>
            <p className="text-gray-600 mb-4">关系图谱可视化功能开发中...</p>
            <p className="text-sm text-gray-500">将展示角色之间的复杂关系网络</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharacterSettingPage;